/**
 * useGeolocation Hook
 * Custom hook for getting GPS coordinates
 */

import { useState, useCallback, useEffect } from 'react'
import type { GPSLocation } from '../types'

interface GeolocationState {
  location: GPSLocation | null
  isLoading: boolean
  error: string | null
}

interface UseGeolocationReturn extends GeolocationState {
  loading: boolean // Alias for isLoading
  getLocation: () => Promise<GPSLocation | null>
  refresh: () => Promise<GPSLocation | null> // Alias for getLocation
  clearError: () => void
}

export function useGeolocation(): UseGeolocationReturn {
  const [state, setState] = useState<GeolocationState>({
    location: null,
    isLoading: false,
    error: null,
  })

  const getLocation = useCallback(async (): Promise<GPSLocation | null> => {
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setState(prev => ({
        ...prev,
        error: 'Geolocation is not supported by your browser',
      }))
      return null
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }))

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location: GPSLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString(),
          }
          setState({
            location,
            isLoading: false,
            error: null,
          })
          resolve(location)
        },
        (error) => {
          let errorMessage = 'Failed to get location'
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable location access.'
              break
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.'
              break
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.'
              break
          }
          setState({
            location: null,
            isLoading: false,
            error: errorMessage,
          })
          resolve(null)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      )
    })
  }, [])

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  // Auto-fetch location on mount
  useEffect(() => {
    getLocation()
  }, [getLocation])

  return {
    ...state,
    loading: state.isLoading,
    getLocation,
    refresh: getLocation,
    clearError,
  }
}
